import {
  Divider,
  HStack,
  Heading,
  IconButton,
  VStack,
  Text,
} from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Show } from '$/components/common/Flow/Show';
import { Icon } from '$/components/common/Icon';
import { FavoriteFolderSection } from '$/components/core/Editor/ColorSelectionSidebar/FavoriteFolderSection';
import { useGroupedFavorites } from '$/pages/DashboardPages/pages/Favorite/hooks/useGroupedFavorites';
import { MaterialType } from '$/services/mapper/uses';
import { Material } from '$/services/usecases/materials';
import { DEFAULTFOLDERNAME, useFavoriteStore } from '$/stores/useFavoriteStore';

interface Props {
  materialTypes?: MaterialType[];
  activeMaterialId?: string;
  onClickItem: (material: Material) => void;
}

export const FavoriteFolderView: FC<Props> = ({
  materialTypes,
  activeMaterialId,
  onClickItem,
}) => {
  const { t } = useTranslation();
  const activeFolderId = useFavoriteStore.useActiveFolderId();
  const setActiveFolderId = useFavoriteStore.useSetActiveFolderId();
  const folders = useFavoriteStore.useFavoriteFolders();
  const {
    colorFavorites,
    floorFavorites,
    wallFavorites,
    isLoading,
    allFavorites,
  } = useGroupedFavorites(activeFolderId ?? DEFAULTFOLDERNAME, materialTypes);

  const activeFolder = folders.find(
    (folder) => folder.id === activeFolderId,
  ) ?? { id: DEFAULTFOLDERNAME, name: t('favorites.generalStorage') };

  return (
    <VStack alignItems='flex-start' w='full'>
      <HStack justifyContent='flex-start' px='4'>
        <IconButton
          aria-label={t('general.back')}
          icon={
            <Icon icon='arrow_right' transform='rotate(180deg)' color='text' />
          }
          onClick={() => setActiveFolderId(null)}
          variant='text'
        />
        <Heading as='h2' fontSize='sm' noOfLines={1}>
          {`${t('inspirationEditor.attributes.favorites')}: ${activeFolder.name}`}
        </Heading>
      </HStack>
      <Divider />
      <Show
        when={allFavorites.length > 0 || isLoading}
        fallback={() => (
          <Text px='5' fontSize='sm'>
            {t('favorites.empty.noMatchingFavorites')}
          </Text>
        )}
      >
        <FavoriteFolderSection
          favorites={colorFavorites}
          title={`${colorFavorites.length} ${t('dashboard.welcome.collection.colorTones')}`}
          isLoading={isLoading}
          activeMaterialId={activeMaterialId}
          onClickItem={onClickItem}
        />
        <FavoriteFolderSection
          favorites={wallFavorites}
          title={`${wallFavorites.length} ${t('dashboard.welcome.collection.wallCoverings')}`}
          isLoading={isLoading}
          additionalHideCondition={
            !materialTypes || !materialTypes?.includes('wall')
          }
          activeMaterialId={activeMaterialId}
          onClickItem={onClickItem}
        />
        <FavoriteFolderSection
          favorites={floorFavorites}
          title={`${floorFavorites.length} ${t('dashboard.welcome.collection.floorCoverings')}`}
          isLoading={isLoading}
          additionalHideCondition={
            !materialTypes || !materialTypes?.includes('floor')
          }
          activeMaterialId={activeMaterialId}
          onClickItem={onClickItem}
        />
      </Show>
    </VStack>
  );
};
